import { Fragment, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { VuiAppContent, VuiAppLayout, VuiFlexContainer, VuiFlexItem, VuiSpacer } from "../ui";
import { useSearchContext } from "../contexts/SearchContext";
import { useChatContext } from "../contexts/ChatContext";
import { AppHeader } from "./chrome/AppHeader";
import { QueryInput } from "./controls/QueryInput";
import { ExampleQuestions } from "./controls/ExampleQuestions";
import { ChatItem } from "./ChatItem";
import "./chatView.scss";
import { useSearchParams } from "react-router-dom";

export const ChatView = () => {
  const {
    isSearching,
    isSummarizing,
    searchValue,
    setSearchValue,
    searchError,
    searchResults,
    summarizationError,
    summarizationResponse,
    summarizationQuestion
  } = useSearchContext();

  const [searchParams] = useSearchParams();
  document.title = searchParams.get('title') ?? "Melvil - Your Librarian";
  const urlPrefix = searchParams.get('url_prefix') ?? '';
  const color = searchParams.get('color') ?? '#264cd6';
  const questionsIntro = searchParams.get('questions_intro') ?? 'Try out these example questions';
  const questionsStr = searchParams.get('questions') ?? '';
  const questions = (questionsStr.length > 0) ? questionsStr.split('|') : ['Can you recommend some interesting resources?'];
  const cta = searchParams.get('cta') ?? 'Send';
  const placeholder = searchParams.get('placeholder') ?? 'Chat with your AI assistant';
  const logoUrl = searchParams.get('logo_url') ?? undefined;
  const header = searchParams.get('header') ?? undefined;
  const headerUrl = searchParams.get('header_url') ?? '#';
  const headerPadding = header ? '46px' : '0';
  const parentOrigin = searchParams.get('parent_origin') ?? '';
  const linkTarget = searchParams.get('link_target') ?? '_blank';
  const closeBtn = (searchParams.get('close_button') === 'true');

  const wlStyle = `
    <style>
      .vuiAppLayout {
        padding-top: ${headerPadding};
      }
      .vuiAppHeader {
        background-color: ${color};
      }
      .vuiTextColor--primary,
      .vuiLink 
      {
        color: ${color};
      }
      .vuiTopicButton {
        &:hover {
          box-shadow: rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px, ${color} 0 0 1px 1px;
        }
      }
      .vuiButtonPrimary--primary {
        background-color: ${color};
        border-color: ${color};
      }
      .vuiSearchInput__input {
        &:focus-visible {
          outline-color: ${color};
        }
      }
      .chatSearchResultBanner {
        background-color: ${color};
      }
    </style>
  `;

  const { onSubmitChat, chatHistory } = useChatContext();
  const [isReferencesOpen, setIsReferencesOpen] = useState(false);

  const appLayoutRef = useRef<HTMLDivElement>(null);
  const isScrolledToBottomRef = useRef(true);

  const updateScrollPosition = () => {
    // Scrolling UX rules:
    // * Scroll down if the last recorded scroll position was already
    //   at the bottom of the list and if the last question has resolved
    //   to an answer.
    // * If the user has scrolled to another position, then don’t
    //   auto-scroll.

    // This way if the user takes control of the scroll position, they
    // remain in control. If the user hasn’t taken control of the scroll
    // position, then the scroll feels stable (by staying at the
    // bottom) as opposed to scrolling unpredictably through the list
    // as questions resolve.
    if (isScrolledToBottomRef.current) {
      // Scroll to the bottom of the chat to keep the latest turn in view.
      appLayoutRef.current?.scrollTo({
        left: 0,
        top: appLayoutRef.current?.scrollHeight,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    const layoutNode = appLayoutRef.current;

    const onScrollContent = (e: Event) => {
      const isScrolledToBottom = appLayoutRef.current
        ? Math.abs(
            appLayoutRef.current.scrollHeight - appLayoutRef.current.clientHeight - appLayoutRef.current.scrollTop
          ) < 50
        : true;

      isScrolledToBottomRef.current = isScrolledToBottom;
    };

    // We're going to track the scroll position, which will determine
    // or not the user is at the bottom of the chat.
    layoutNode?.addEventListener("scroll", onScrollContent);

    return () => {
      layoutNode?.removeEventListener("scroll", onScrollContent);
    };
  }, []);

  useEffect(updateScrollPosition, [
    isSearching,
    isSummarizing,
    searchError,
    summarizationError,
    summarizationResponse,
    summarizationQuestion,
    chatHistory,
    isReferencesOpen
  ]);

  const chatItems = chatHistory.map((turn, index) => {
    const { question, answer } = turn;
    return <ChatItem key={index} question={question} answer={answer} />;
  });

  // If a query has been recorded to the queryState then it's either
  // outstanding or has resolved to an answer or error. Either way, we
  // want to surface it in the chat history.
  if (isSearching || isSummarizing || summarizationResponse || searchError || summarizationError) {
    chatItems.push(
      <ChatItem
        isLoading={isSearching || !summarizationResponse}
        isSummarizing={isSummarizing}
        question={summarizationQuestion}
        answer={summarizationResponse}
        searchResults={searchResults}
        error={searchError && <>{searchError.message}</>}
        isReferencesOpen={isReferencesOpen}
        setIsReferencesOpen={setIsReferencesOpen}
        urlPrefix={urlPrefix}
        linkTarget={linkTarget}
        parentOrigin={parentOrigin}
      />
    );
  }

  const chatInputClasses = classNames("chatInputContainer");

  const hasContent = isSearching || isSummarizing || chatHistory.length > 0 || summarizationQuestion;

  // Listen for parent window messages
  function onRecievedMessage(event: MessageEvent) {
    // console.log(event.origin);
    // console.log(parentOrigin);
    if (event.origin !== parentOrigin) {
      return;
    }
    if (event.data.prompt) {
      onSubmitChat(event.data.prompt);
    } else if (event.data.reset) {
      window.location.reload();
    }
  }

  useEffect(function () {
    window.addEventListener("message", onRecievedMessage);
    return function () {
      window.removeEventListener("message", onRecievedMessage);
    };
  });

  return (
      <>

        <span dangerouslySetInnerHTML={{__html: wlStyle}}/>

        <AppHeader logoUrl={logoUrl} title={header} url={headerUrl} />

        <VuiAppLayout ref={appLayoutRef}>
          <VuiAppContent className="appContent" fullWidth={false} padding="none">
            <VuiFlexContainer direction="column" className="appContent__inner" spacing="none">

              <VuiFlexItem grow={1} className="appBody">
                <VuiSpacer size="xl"/>

                {!hasContent ? (
                    <ExampleQuestions questions={questions} intro={questionsIntro} />
                ) : (
                    chatItems.map((item, index) => (
                        <Fragment key={index}>
                          {item}
                          {index < chatItems.length - 1 ? <VuiSpacer size="m"/> : <VuiSpacer size="xl"/>}
                        </Fragment>
                    ))
                )}
              </VuiFlexItem>

              <VuiFlexItem grow={false} shrink={false} className={chatInputClasses}>
                <QueryInput
                    placeholder={placeholder}
                    buttonLabel={cta}
                    query={searchValue}
                    setQuery={setSearchValue}
                    isDisabled={isSearching || isSummarizing}
                    search={() => onSubmitChat()}
                    color={color}
                    parentOrigin={parentOrigin}
                    closeBtn={closeBtn}
                />
              </VuiFlexItem>

            </VuiFlexContainer>
          </VuiAppContent>
        </VuiAppLayout>
      </>
  );
};
